import {useMemo, useState} from 'react';
import type {ChangeEvent, ReactElement} from 'react';
import {Button, Size as ButtonSize, Type as ButtonType} from '@Components/button/button';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE} from '@Components/dropdown-v2/dropdown.types';
import {Icon} from '@Components/icon-v2/icon';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text/text';
import {ToggleSwitch, ToggleSwitchV2Size} from '@Components/toggle-switch-v2/toggle-switch';
import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import type {AgentOverrideRow} from './ai-chat-super-user-options.types';
import {clearSuperUserVariantOverrides, getInitialAgents, setSuperUserVariantOverride} from './ai-chat-super-user-options.library';
import styles from './ai-chat-super-user-options.module.scss';

const WEIGHTED_PICK_VALUE = '';

type AgentChangeHandler = (agentType: string) => (event: ChangeEvent<HTMLSelectElement>) => void;

export function AiChatSuperUserOptions(): ReactElement | null {
  const {state, actions} = useAiChat();
  const initialAgents = useMemo<AgentOverrideRow[]>(() => getInitialAgents(), []);
  const [agents, setAgents] = useState<AgentOverrideRow[]>(initialAgents);
  const [error, setError] = useState<string | null>(null);

  if (!state.isSuperUser) {
    return null;
  }

  const handleChange: AgentChangeHandler =
    (agentType: string) =>
    (event: ChangeEvent<HTMLSelectElement>): void => {
      const value = event.target.value;
      void setSuperUserVariantOverride(agentType, value).then(
        () => {
          setError(null);
          setAgents((current) =>
            current.map((row) => (row.agentType === agentType ? {...row, currentAlias: value === WEIGHTED_PICK_VALUE ? null : value} : row))
          );
        },
        () => {
          setError(window.i18next.t('pmwjs_variant_save_failed'));
        }
      );
    };

  const handleClearAll = (): void => {
    void clearSuperUserVariantOverrides().then(
      () => {
        setError(null);
        setAgents((current) => current.map((row) => ({...row, currentAlias: null})));
      },
      () => {
        setError(window.i18next.t('pmwjs_variant_save_failed'));
      }
    );
  };

  const handleShowDeletedChange = (): void => {
    actions.setShowDeletedMessages(!state.showDeletedMessages);
  };

  return (
    <Dropdown
      id="ai-chat-super-user-options"
      alignment={ALIGNMENT_TYPE.BOTTOM_END}
      popUpHasCustomWidth
      popupClassName={styles.popup}
      doCloseOnInnerClick={false}
      selector={renderSelector()}
      popup={renderPopup({
        showDeleted: state.showDeletedMessages,
        onShowDeletedChange: handleShowDeletedChange,
        agents,
        onChangeAgent: handleChange,
        onClearAll: handleClearAll,
        error,
      })}
    />
  );
}

function renderSelector(): ReactElement {
  return (
    <Icon
      icon="icon-tools"
      shape={IconShape.SQUARE}
      type={IconType.GHOST_UNCOLORED}
      size={IconSize.SIZE_ICON_20}
      isUsedAsButton
      ariaLabel={window.i18next.t('pmwjs_super_user_options_title')}
    />
  );
}

interface PopupProps {
  showDeleted: boolean;
  onShowDeletedChange: () => void;
  agents: AgentOverrideRow[];
  onChangeAgent: AgentChangeHandler;
  onClearAll: () => void;
  error: string | null;
}

function renderPopup(props: PopupProps): ReactElement {
  return (
    <div className={styles.body}>
      <Text val={window.i18next.t('pmwjs_super_user_options_title')} size={TextSize.SM} bold className={styles.heading} />
      <div className={styles.toggleRow}>
        <ToggleSwitch
          id="ai-chat-show-deleted-messages"
          isChecked={props.showDeleted}
          onChange={props.onShowDeletedChange}
          size={ToggleSwitchV2Size.SMALL}
          elementTitle={window.i18next.t('pmwjs_show_deleted_messages')}
        />
        <Text val={window.i18next.t('pmwjs_show_deleted_messages')} size={TextSize.SM} className={styles.toggleLabel} />
      </div>
      {renderVariantSection(props.agents, props.onChangeAgent, props.onClearAll)}
      {renderError(props.error)}
    </div>
  );
}

function renderVariantSection(agents: AgentOverrideRow[], onChange: AgentChangeHandler, onClearAll: () => void): ReactElement | null {
  if (agents.length === 0) {
    return null;
  }
  return (
    <>
      <div className={styles.divider} aria-hidden />
      <Text val={window.i18next.t('pmwjs_system_prompt_variants')} size={TextSize.SM} bold className={styles.heading} />
      {renderRows(agents, onChange)}
      <div className={styles.footer}>
        <Button type={ButtonType.SECONDARY} size={ButtonSize.SMALL} text={window.i18next.t('pmwjs_clear_all')} onClick={onClearAll} />
        <Text val={window.i18next.t('pmwjs_super_user_overrides_note')} size={TextSize.XS} className={styles.note} />
      </div>
    </>
  );
}

function renderRows(agents: AgentOverrideRow[], handleChange: AgentChangeHandler): ReactElement {
  return (
    <ul className={styles.list}>
      {agents.map((row) => {
        const activeOptions = row.options.filter((option) => option.isActive);
        return (
          <li key={row.agentType} className={styles.variantRow}>
            <code className={styles.agentName}>{row.agentType}</code>
            <select className={styles.select} value={row.currentAlias ?? WEIGHTED_PICK_VALUE} onChange={handleChange(row.agentType)}>
              <option value={WEIGHTED_PICK_VALUE}>{window.i18next.t('pmwjs_variant_weighted_pick')}</option>
              {row.options.map((option) => (
                <option key={option.idAiPrompt} value={option.alias ?? ''} disabled={!option.isActive}>
                  {formatVariantOptionLabel(option.alias, option.weightPct, option.isActive)}
                </option>
              ))}
            </select>
            <span className={styles.meta}>{getVariantCountLabel(activeOptions.length)}</span>
          </li>
        );
      })}
    </ul>
  );
}

function renderError(error: string | null): ReactElement | null {
  if (error === null) {
    return null;
  }
  return <p className={styles.error}>{error}</p>;
}

function formatVariantOptionLabel(alias: string | null, weightPct: number, isActive: boolean): string {
  const aliasLabel = alias ?? window.i18next.t('pmwjs_variant_unnamed');
  const suffix = isActive ? '' : ` · ${window.i18next.t('pmwjs_variant_inactive_suffix')}`;
  return `${aliasLabel} · ${weightPct}%${suffix}`;
}

function getVariantCountLabel(count: number): string {
  const key = count === 1 ? 'pmwjs_variant_count_one' : 'pmwjs_variant_count_other';
  return window.i18next.t(key, {count});
}
